import PropTypes from 'prop-types';
import React, { useState } from 'react';
import StarIcon from '@ux/icon/star';
import StarOutline from '@ux/icon/star-outline';
import Star from './star';

/**
 * Determines if checked based on rating and hovered
 *
 * @param {number} index of Star to retrieve className for.
 * @param {boolean} isHovering determines if star is active hovered
 * @param {number} hovered current hovered star
 * @param {number} rating current star rating
 * @returns {boolean} determins if checked
 * @private
 */
function isChecked({ index, isHovering, hovered, rating }) {
  const value = isHovering ? hovered : rating;
  return index <= value;
}


/**
 * UXCore based Stars. Stars will always start with
 * an initial state given by the `rating` and `count` props, isHovering and hovered index(default is 0)
 * @param {Object} props Props
 * @param {string} [props.title] label for whole element
 * @param {number} [props.rating] selected number of stars
 * @param {string} [props.text] lable to render for rating and total count
 * @param {number} [props.count] total number of stars
 * @param {function} props.onRatingClick external callback to listen for state change
 * @displayName Stars
 * @returns {React.ReactElement} Stars react component
 */
export default function Stars({
  title,
  rating: initialRating,
  text,
  count,
  onRatingClick,
  tabIndex,
  ariaLabels
}) {
  const [rating, setRating] = useState(initialRating);
  const [isHovering, setHovering] = useState(false);
  const [hovered, setHovered] = useState(initialRating);

  return (
    <div className='ux-stars'
      role='slider'
      tabIndex={ tabIndex }
      aria-label={ ariaLabels.rateThis }
      aria-valuenow={ rating }
      aria-valuemin={ rating }
      aria-valuemax={ count }
      aria-valuetext={ `${ rating } out of ${ count } stars` }>
      <p className='ux-stars-title'>
        { title }
      </p>
      <span
        className='ux-stars-rating'>
        { Array.from(Array(count).keys()).map((_, idx) => {
          const index = idx + 1;
          const checked = isChecked({ index, isHovering, hovered, rating });
          const starType = checked ? <StarIcon /> : <StarOutline />;
          return (
            <Star
              key={ `star-${index}` }
              ariaLabels={ ariaLabels.star }
              tabIndex={ tabIndex }
              count={ count }
              index={ index }
              className={ checked ? 'checked' : 'unchecked' }
              star={ starType }
              hovered={ hovered }
              setHovered={ setHovered }
              setHovering={ setHovering }
              setRating={ setRating }
              onRatingClick={ onRatingClick }
            />
          );
        })}
      </span>
      <span className='ux-stars-numbers'>
        { text ? `${ rating } / ${ count }` : '' }
      </span>
    </div>
  );
}

Stars.propTypes = {
  /** Flag stating whether an <h1> tag should be rendered indicating the current rating.*/
  text: PropTypes.bool,
  /** The current star rating to be rendered*/
  rating: PropTypes.number,
  /** Total number of stars*/
  count: PropTypes.number,
  /** Function to be called when a star is clicked. Callback gets the number of the clicked star on passed in.*/
  onRatingClick: PropTypes.func.isRequired,
  /** Title of the star rating */
  title: PropTypes.string,
  /** Tab index for whole React Element*/
  tabIndex: PropTypes.oneOf([-1, 0]),
  /** Overridable aria-label text */
  ariaLabels: PropTypes.shape({
    /** aria-label for outer rating */
    rateThis: PropTypes.string,
    /** Aria-label for stars */
    star: PropTypes.string
  })
};

Stars.defaultProps = {
  ariaLabels: {
    rateThis: 'Rate this',
    star: 'Star'
  },
  tabIndex: 0,
  text: true,
  rating: 3,
  count: 5,
  title: ''
};
